import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { tokenStorage } from '@/utils/storage/tokenStorage'
import { httpClient } from '@/utils/http/client'
import { setSentryUser, addSentryBreadcrumb } from '@/utils/sentry'
import { identifyUser, resetUser, trackEvent } from '@/utils/analytics'
import { useSettingsStore } from '@/stores/settings'
import { useBillingStore } from '@/stores/billing'
import { useChatStore } from '@/stores/chat'
import type {
  User,
  LoginCredentials,
  RegisterData,
  AuthResponse,
  ConfirmEmailChangeData,
  ForgotPasswordData,
  ResetPasswordData,
  VerifyEmailData,
  ResendVerificationData,
  UpdateProfileData,
  DeleteAccountData,
  TwoFactorChallenge,
} from '@/types/auth'
import type { AxiosError } from 'axios'
import type { ApiErrorResponse } from '@/types/errors'

/**
 * Response type for token refresh endpoint.
 * Note: refreshToken is not included since it's set as httpOnly cookie.
 */
interface RefreshTokenResponse {
  token: string
  expiresIn: number
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(tokenStorage.getToken())
  const loading = ref(false)
  const error = ref<string | null>(null)
  const apiError = ref<ApiErrorResponse | null>(null)
  const tokenExpiresAt = ref<number | null>(null)
  const refreshTimerId = ref<number | null>(null)
  const pendingTwoFactorRememberMe = ref<boolean | null>(null)

  // Initialization state - tracks whether auth initialization has completed
  const isInitializing = ref(true)
  let initializationPromiseResolve: (() => void) | null = null
  const initializationPromise = new Promise<void>((resolve) => {
    initializationPromiseResolve = resolve
  })

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isEmailVerified = computed(() => user.value?.emailVerified ?? false)

  // Private helper to schedule token refresh
  function scheduleTokenRefresh(expiresIn: number) {
    // Clear existing timer
    if (refreshTimerId.value) {
      clearTimeout(refreshTimerId.value)
    }

    // Schedule refresh 1 minute before expiration
    const refreshTime = (expiresIn - 60) * 1000
    if (refreshTime > 0) {
      refreshTimerId.value = window.setTimeout(() => {
        refreshToken()
      }, refreshTime)
    }
  }

  // Private helper to set auth data
  function setAuth(authData: AuthResponse) {
    resetAccountState()
    token.value = authData.tokens.token
    user.value = authData.user
    tokenStorage.setToken(authData.tokens.token)

    // Note: refresh token is set as httpOnly cookie by backend, not stored in JS

    // Calculate expiration time
    tokenExpiresAt.value = Date.now() + authData.tokens.expiresIn * 1000

    // Schedule automatic refresh
    scheduleTokenRefresh(authData.tokens.expiresIn)

    // Apply user settings from API (overrides localStorage)
    if (authData.user.settings) {
      const settingsStore = useSettingsStore()
      settingsStore.applyFromApi(authData.user.settings)
    }

    // Set Sentry user context for error tracking
    setSentryUser({
      id: authData.user.id,
      email: authData.user.email,
    })

    // Identify user for analytics tracking
    identifyUser({
      id: authData.user.id,
      email: authData.user.email,
    })
  }

  // Private helper to clear auth data
  function clearAuth() {
    resetAccountState()
    token.value = null
    user.value = null
    tokenExpiresAt.value = null
    pendingTwoFactorRememberMe.value = null
    tokenStorage.clear()

    // Clear refresh timer
    if (refreshTimerId.value) {
      clearTimeout(refreshTimerId.value)
      refreshTimerId.value = null
    }

    // Reset settings store (keeps localStorage for landing page)
    const settingsStore = useSettingsStore()
    settingsStore.reset()

    // Clear Sentry user context
    setSentryUser(null)

    // Clear analytics user context
    resetUser()
  }

  function resetAccountState(): void {
    useBillingStore().resetAccountState()
    useChatStore().resetAccountState()
  }

  // Actions
  async function login(credentials: LoginCredentials): Promise<TwoFactorChallenge | null> {
    loading.value = true
    error.value = null
    apiError.value = null

    addSentryBreadcrumb('auth', 'Login attempt', { email: credentials.email })

    try {
      const response = await httpClient.post<AuthResponse | TwoFactorChallenge>('/auth/login', credentials, {
        withCredentials: true, // Include cookies for refresh token
      })

      if ('twoFactorRequired' in response.data) {
        pendingTwoFactorRememberMe.value = credentials.rememberMe ?? false
        addSentryBreadcrumb('auth', 'Two-factor challenge required')
        return response.data
      }

      pendingTwoFactorRememberMe.value = null
      setAuth(response.data)

      // Store remember me preference
      tokenStorage.setRememberMe(credentials.rememberMe ?? false)

      addSentryBreadcrumb('auth', 'Login successful')
      trackEvent('user_login', { method: 'email' })
      return null
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.message || 'Login failed'
      addSentryBreadcrumb('auth', 'Login failed', { error: error.value }, 'error')
      throw err
    } finally {
      loading.value = false
    }
  }

  async function completeTwoFactor(challengeToken: string, code: string): Promise<void> {
    loading.value = true
    error.value = null
    apiError.value = null

    try {
      const response = await httpClient.post<AuthResponse>(
        '/auth/two-factor',
        { challengeToken, code },
        { withCredentials: true },
      )
      setAuth(response.data)
      tokenStorage.setRememberMe(pendingTwoFactorRememberMe.value ?? false)
      pendingTwoFactorRememberMe.value = null
      addSentryBreadcrumb('auth', 'Two-factor challenge completed')
      trackEvent('user_login', { method: 'email_2fa' })
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.message || 'Two-factor verification failed'

      if (axiosError.response?.data?.error === 'auth.two_factor_challenge_invalid') {
        pendingTwoFactorRememberMe.value = null
      }

      throw err
    } finally {
      loading.value = false
    }
  }

  async function register(data: RegisterData): Promise<void> {
    loading.value = true
    error.value = null
    apiError.value = null

    try {
      const response = await httpClient.post<AuthResponse>('/auth/register', data, {
        withCredentials: true, // Include cookies for refresh token
      })

      setAuth(response.data)
      trackEvent('user_signup', { method: 'email' })
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.message || 'Registration failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    loading.value = true
    error.value = null

    addSentryBreadcrumb('auth', 'Logout initiated')

    try {
      // Backend reads refresh token from httpOnly cookie
      await httpClient.post(
        '/auth/logout',
        {},
        {
          withCredentials: true,
        }
      )
      addSentryBreadcrumb('auth', 'Logout successful')
      trackEvent('user_logout')
    } catch (err) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', err)
      addSentryBreadcrumb('auth', 'Logout API failed', undefined, 'warning')
    } finally {
      clearAuth()
      loading.value = false
    }
  }

  async function refreshToken(): Promise<void> {
    // Refresh token is sent automatically via httpOnly cookie
    try {
      const response = await httpClient.post<RefreshTokenResponse>(
        '/auth/refresh',
        {}, // Empty body - refresh token is in cookie
        {
          withCredentials: true,
        }
      )

      token.value = response.data.token
      tokenStorage.setToken(response.data.token)
      tokenExpiresAt.value = Date.now() + response.data.expiresIn * 1000

      // New refresh token is set as httpOnly cookie by backend (rotation)

      // Schedule next refresh
      scheduleTokenRefresh(response.data.expiresIn)
    } catch (err) {
      console.error('Token refresh failed:', err)
      clearAuth()
      throw err
    }
  }

  async function forgotPassword(data: ForgotPasswordData): Promise<void> {
    loading.value = true
    error.value = null
    apiError.value = null

    try {
      await httpClient.post('/auth/forgot-password', data)
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.message || 'Request failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(data: ResetPasswordData): Promise<void> {
    loading.value = true
    error.value = null
    apiError.value = null

    try {
      await httpClient.post('/auth/reset-password', data)
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.message || 'Password reset failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function verifyEmail(data: VerifyEmailData): Promise<void> {
    loading.value = true
    error.value = null
    apiError.value = null

    try {
      // The endpoint answers with a message only - it never returns the user
      await httpClient.post<{ message: string }>('/auth/verify-email', data)

      // Reflect the verified flag locally so the banner clears immediately.
      // Deliberately NOT fetchCurrentUser(): its failure path calls
      // clearAuth() and sets error before throwing, so a transient /auth/me
      // blip would sign the user out even though verification succeeded.
      if (user.value) {
        user.value = { ...user.value, emailVerified: true }
      }
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.message || 'Email verification failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Ask the API to send a fresh verification link.
   *
   * The endpoint answers with a uniform message for unknown, unverified and
   * already verified addresses, so there is no response payload to read.
   */
  async function resendVerification(data: ResendVerificationData): Promise<void> {
    loading.value = true
    error.value = null
    apiError.value = null

    try {
      await httpClient.post('/auth/resend-verification', data)
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.message || 'Request failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Confirm a pending email change with the token from the new mailbox.
   *
   * Public on the API side, so this works with or without a session. When
   * there IS a session and it belongs to the account that just moved, the
   * local user is patched from the response - deliberately NOT via
   * fetchCurrentUser(), whose failure path calls clearAuth() and would sign
   * the user out over a transient /auth/me blip after a change that succeeded.
   * The store is not touched when the signed-in account is somebody else: the
   * link can legitimately be opened in a browser signed in as another user.
   */
  async function confirmEmailChange(data: ConfirmEmailChangeData): Promise<string> {
    loading.value = true
    error.value = null
    apiError.value = null

    try {
      const response = await httpClient.post<{
        message: string
        userId: string
        email: string
      }>('/auth/confirm-email-change', data)

      if (user.value && user.value.id === response.data.userId) {
        user.value = { ...user.value, email: response.data.email, emailVerified: true }
      }

      return response.data.email
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.error || 'Email change confirmation failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Update the authenticated user's profile.
   *
   * The endpoint answers with the stored profile, so the local user is
   * refreshed from the response rather than from the submitted draft: the
   * API trims the name and turns a blank one into null.
   */
  async function updateProfile(data: UpdateProfileData): Promise<void> {
    loading.value = true
    error.value = null
    apiError.value = null

    try {
      const response = await httpClient.patch<{ name: string | null }>('/users/me/profile', data)

      if (user.value) {
        user.value = { ...user.value, name: response.data.name }
      }
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.message || 'Failed to update profile'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Permanently delete the current account.
   *
   * Local state is cleared only after the API confirms: wiping the session on
   * a failed call would leave the user logged out of an account that still
   * exists, with an active subscription they can no longer reach.
   */
  async function deleteAccount(data: DeleteAccountData): Promise<void> {
    loading.value = true
    error.value = null
    apiError.value = null

    try {
      // The password travels in the request body; axios puts a DELETE body
      // under `data`. withCredentials so the server can clear the refresh
      // token cookie it set.
      await httpClient.delete('/users/me', {
        data,
        withCredentials: true,
      })

      trackEvent('user_account_deleted')
      clearAuth()
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.message || 'Account deletion failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchCurrentUser(): Promise<void> {
    if (!token.value) return

    loading.value = true
    error.value = null
    apiError.value = null

    try {
      const response = await httpClient.get<{ user: User }>('/auth/me')
      if (user.value?.id !== response.data.user.id) {
        resetAccountState()
      }
      user.value = response.data.user

      // Apply user settings from API (overrides localStorage)
      if (response.data.user.settings) {
        const settingsStore = useSettingsStore()
        settingsStore.applyFromApi(response.data.user.settings)
      }
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>
      apiError.value = axiosError.response?.data ?? null
      error.value = axiosError.response?.data?.message || 'Failed to fetch user'
      clearAuth()
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Initialize auth state on page load.
   *
   * If we have an access token in memory, try to use it.
   * If not, attempt to refresh using the httpOnly cookie.
   * This handles session restoration after page reload.
   */
  async function initialize(): Promise<void> {
    // If already initialized, skip
    if (!isInitializing.value) {
      return
    }

    const storedToken = tokenStorage.getToken()

    try {
      if (storedToken) {
        // We have an in-memory token, verify it's still valid
        token.value = storedToken
        try {
          await fetchCurrentUser()

          // If user has remember me enabled, try to refresh token proactively
          if (tokenStorage.getRememberMe()) {
            await refreshToken()
          }
        } catch {
          clearAuth()
        }
      } else {
        // No in-memory access token, but we might have a refresh token cookie
        // Try to refresh - the httpOnly cookie will be sent automatically
        try {
          await refreshToken()
          // If refresh succeeded, we have a new token - now fetch user
          if (token.value) {
            await fetchCurrentUser()
          }
        } catch {
          // No valid refresh token cookie - user is not authenticated
          // This is expected on first visit or after logout, so silently ignore
        }
      }
    } finally {
      // Mark initialization as complete
      isInitializing.value = false
      if (initializationPromiseResolve) {
        initializationPromiseResolve()
      }
    }
  }

  /**
   * Wait for auth initialization to complete.
   * Use this in router guards to ensure auth state is ready before checking authentication.
   */
  async function waitForInitialization(): Promise<void> {
    if (!isInitializing.value) {
      return
    }
    return initializationPromise
  }

  function clearError() {
    error.value = null
    apiError.value = null
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    apiError,
    tokenExpiresAt,
    isInitializing,
    // Getters
    isAuthenticated,
    isEmailVerified,
    // Actions
    login,
    completeTwoFactor,
    register,
    logout,
    refreshToken,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    confirmEmailChange,
    updateProfile,
    deleteAccount,
    fetchCurrentUser,
    initialize,
    waitForInitialization,
    clearError,
  }
})
